import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, base_url } from "../../config/config";
import { Search, Plus, Trash2, CheckCircle, AlertCircle, X } from "lucide-react";

const Bestallung = () => {
  const navigate = useNavigate();
  const [firms, setFirms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [bestellungItems, setBestellungItems] = useState([
    {
      Anr: "",
      description: "",
      menge: "",
      unit: "",
      unitId: "",
      artikelNumber: "",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchFirms();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchFirms = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getFirma`);
      const data = await response.json();
      setFirms(data.data);
    } catch (error) {
      setError("Failed to load companies. Please try again later.");
      console.error("Error fetching firms:", error);
    }
  };

  const filteredFirms = firms.filter(
    (firm) =>
      searchTerm &&
      (firm.bezeichnung.toLowerCase().includes(searchTerm.toLowerCase()) ||
        firm.konto.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const handleSelectFirm = (firm) => {
    setSelectedFirm(firm);
    setSearchTerm(firm.bezeichnung);
    setShowDropdown(false);
    setConfirmed(false);
  };

  const handleConfirm = () => {
    setConfirmed(true);
  };

  const handleClear = () => {
    setSelectedFirm(null);
    setSearchTerm("");
    setConfirmed(false);
    setBestellungItems([
      {
        Anr: "",
        description: "",
        menge: "",
        unit: "",
        unitId: "",
        artikelNumber: "",
      },
    ]);
    setSuccess("");
  };

  const handleAddItem = () => {
    setBestellungItems([
      ...bestellungItems,
      {
        Anr: "",
        description: "",
        menge: "",
        unit: "",
        unitId: "",
        artikelNumber: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = bestellungItems.filter((_, i) => i !== index);
    setBestellungItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...bestellungItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setBestellungItems(newItems);
  };

  const handleSubmitBestellung = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${base_url}/v1/bestellung/createBestellung`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firmaId: selectedFirm._id,
            details: bestellungItems,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to create bestellung");

      const result = await response.json();

      setSuccess("Bestellung created successfully!");

      // Navigate to details page after successful creation
      navigate(`/BestellungDetails/${result.bestellung._id}`);
    } catch (error) {
      setError("Failed to create bestellung. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const progress = confirmed ? 2 : selectedFirm ? 1 : 0;
  const maxSteps = 2;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Create New Order</h1>
            <span className="text-sm font-medium text-indigo-600">
              Step {progress + 1} of {maxSteps + 1}
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all duration-300 ease-in-out"
              style={{ width: `${(progress / maxSteps) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Company Search Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {!selectedFirm ? "Step 1: Select Company" : "Selected Company"}
            </h2>
            {selectedFirm && (
              <button
                onClick={handleClear}
                className="flex items-center text-sm text-gray-500 hover:text-red-500 transition-colors"
              >
                <X size={16} className="mr-1" /> Clear
              </button>
            )}
          </div>

          {!selectedFirm ? (
            <div className="search-container relative">
              <div className="flex items-center border-2 border-gray-300 rounded-lg focus-within:border-indigo-500 transition-colors overflow-hidden">
                <Search size={20} className="ml-3 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    if (selectedFirm && selectedFirm.bezeichnung !== e.target.value) {
                      setSelectedFirm(null);
                      setConfirmed(false);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search by company name or account number"
                  className="w-full px-3 py-3 outline-none text-gray-700"
                />
              </div>

              {showDropdown && searchTerm && filteredFirms.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-auto divide-y divide-gray-100">
                  {filteredFirms.map((firm) => (
                    <div
                      key={firm._id}
                      onClick={() => handleSelectFirm(firm)}
                      className="px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {firm.bezeichnung}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Account: {firm.konto}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {showDropdown && searchTerm && filteredFirms.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-4 text-center text-gray-500">
                  No companies found matching "{searchTerm}"
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900">
                  {selectedFirm.bezeichnung}
                </h4>
                <p className="text-gray-600 mt-2">
                  Account: {selectedFirm.konto}
                </p>
              </div>

              {!confirmed ? (
                <button
                  onClick={handleConfirm}
                  className="w-full flex items-center justify-center bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  <CheckCircle size={18} className="mr-2" />
                  Confirm Selection & Continue
                </button>
              ) : (
                <div className="flex items-center text-green-600">
                  <CheckCircle size={18} className="mr-2" />
                  <span>Company confirmed</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bestellung Form */}
        {confirmed && selectedFirm && (
          <div className="bg-white rounded-xl shadow-lg p-6 transition-all duration-300">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Step 2: Create Order
              </h2>
              <button
                onClick={handleAddItem}
                className="flex items-center justify-center bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                <Plus size={18} className="mr-1" /> Add Article
              </button>
            </div>

            <div className="space-y-6">
              {bestellungItems.map((item, index) => (
                <div 
                  key={index} 
                  className="p-5 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow relative group"
                >
                  <div className="absolute right-4 top-4">
                    {bestellungItems.length > 1 && (
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove item"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  <h3 className="text-lg font-medium text-gray-900 mb-4">Article #{index + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        rows="2"
                        placeholder="Enter item description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ANR
                      </label>
                      <input
                        type="text"
                        value={item.Anr}
                        onChange={(e) =>
                          handleItemChange(index, "Anr", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Enter ANR"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Article Number
                      </label>
                      <input
                        type="text"
                        value={item.artikelNumber}
                        onChange={(e) =>
                          handleItemChange(index, "artikelNumber", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Enter article number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={item.menge}
                        onChange={(e) =>
                          handleItemChange(index, "menge", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Enter quantity"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <select
                        value={item.unit}
                        onChange={(e) =>
                          handleItemChange(index, "unit", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none bg-white"
                      >
                        <option value="">Select unit</option>
                        <option value="meter">Meter</option>
                        <option value="kg">Kilogram</option>
                        <option value="piece">Piece</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Notifications */}
            {error && (
              <div className="my-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-start">
                <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="my-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded-lg flex items-start">
                <CheckCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-8">
              <button
                onClick={handleSubmitBestellung}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-colors flex items-center justify-center ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Order...
                  </>
                ) : (
                  "Create Order"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bestallung;